import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Code2, Globe, BookOpen, CheckCircle2, Zap, Copy, Monitor } from "lucide-react";
import { useTranslation } from 'react-i18next';
import { useChatbotConfig } from "../../hooks/useChatbotConfig";
import { getWidgetScriptUrl, generateIframeEmbedCode } from "../../config/apiConfig";

export function QuickStartTab() {
  const { t } = useTranslation('business', { keyPrefix: 'communication' });
  const { config } = useChatbotConfig();

  const handleTestChatbot = () => {
    if (!config.businessId) {
      // Simple toast notification
      const toast = document.createElement('div');
      toast.textContent = t('saveConfigurationFirst');
      toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#ef4444;color:white;padding:12px 24px;border-radius:8px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15)';
      document.body.appendChild(toast);
      setTimeout(() => document.body.removeChild(toast), 3000);
      return;
    }
    window.open(`/chatbot-test.html?businessId=${config.businessId}`, '_blank');
  };

  // ✅ KISS UNIVERSAL EMBED CODE - Works everywhere, no manual configuration needed!
  const getUniversalEmbedCode = () => {
    if (!config.businessId) {
      return '<!-- Save your chatbot configuration first to get your embed code -->';
    }
    
    const scriptUrl = getWidgetScriptUrl(config.businessId);
    return `<!-- Wildlife Chatbot Widget - Universal Smart Code -->\n<script src="${scriptUrl}" async></script>`;
  };

  // ✅ IFRAME EMBED CODE - For CMS platforms like Google Sites, WordPress, etc.
  const getIframeEmbedCode = () => {
    if (!config.businessId) {
      return '<!-- Save your chatbot configuration first to get your iframe code -->';
    }
    
    return generateIframeEmbedCode(config.businessId);
  };

  const handleCopyEmbedCode = () => {
    const embedCode = getUniversalEmbedCode();
    navigator.clipboard.writeText(embedCode);
    
    // Simple toast notification
    const toast = document.createElement('div');
    toast.textContent = t('embedCodeCopied');
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#10B981;color:white;padding:12px 24px;border-radius:8px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15)';
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  const handleCopyIframeCode = () => {
    const iframeCode = getIframeEmbedCode();
    navigator.clipboard.writeText(iframeCode);
    
    // Simple toast notification
    const toast = document.createElement('div');
    toast.textContent = 'Iframe code copied to clipboard!';
    toast.style.cssText = 'position:fixed;top:20px;right:20px;background:#10B981;color:white;padding:12px 24px;border-radius:8px;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,0.15)';
    document.body.appendChild(toast);
    setTimeout(() => document.body.removeChild(toast), 3000);
  };

  const isConfigured = Boolean(config.businessId && config.isEnabled);

  return (
    <div className="space-y-6">
      <div className="grid gap-6">
        {/* Hero Section */}
        <div className="text-center p-8 rounded-xl bg-gradient-to-br from-emerald-50 via-emerald-50 to-purple-50 border border-emerald-200/60">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center mx-auto mb-4 shadow-lg shadow-emerald-500/25">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">{t('universalChatbotIntegration')}</h2>
          <p className="text-slate-600 max-w-2xl mx-auto mb-6">
            {t('universalChatbotIntegrationDesc')}
          </p>
          
          {/* Status Badge */}
          <div className="flex justify-center mb-6">
            {isConfigured ? (
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200 px-4 py-2">
                <CheckCircle2 className="w-4 h-4 mr-2" />
                {t('readyToDeploy')}
              </Badge>
            ) : (
              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 px-4 py-2">
                <Code2 className="w-4 h-4 mr-2" />
                {t('saveConfigurationFirst')}
              </Badge>
            )}
          </div>
          
          {/* Prominent Action Buttons */}
          <div className="flex justify-center gap-4">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 shadow-lg shadow-emerald-600/25 px-8"
              onClick={handleTestChatbot}
              disabled={!isConfigured}
            >
              <Globe className="w-5 h-5 mr-2" />
              {t('testIntegration')}
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-slate-200 shadow-sm hover:shadow-md"
              onClick={handleCopyEmbedCode}
            >
              <Code2 className="w-5 h-5 mr-2" />
              {t('copyCode')}
            </Button>
          </div>
        </div>

        {/* Integration Options */}
        <div className="space-y-6">
          <h3 className="text-xl font-bold text-slate-900 mb-4">Integration Options</h3>
          
          {/* JavaScript Widget */}
          <div className="p-6 rounded-lg border border-slate-200/60 bg-slate-50/50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Code2 className="w-5 h-5 text-emerald-600" />
                JavaScript Widget
              </h4>
              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                For custom websites
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mb-4">For websites with custom code access:</p>
            
            <div className="relative">
              <pre className="text-sm bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto border border-slate-700/50 shadow-lg">
                <code className="text-green-400">
{getUniversalEmbedCode()}
                </code>
              </pre>
              <Button
                size="sm"
                className="absolute top-3 right-3 bg-slate-800 hover:bg-slate-700 border border-slate-600"
                onClick={handleCopyEmbedCode}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>
          </div>

          {/* NEW: iframe Embed */}
          <div className="p-6 rounded-lg border border-purple-200/60 bg-purple-50/50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Monitor className="w-5 h-5 text-purple-600" />
                iframe Embed (Universal)
              </h4>
              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                For CMS platforms
              </Badge>
            </div>
            <p className="text-sm text-slate-600 mb-4">For CMS platforms (Google Sites, WordPress, Wix, Squarespace, etc.):</p>
            
            <div className="relative">
              <pre className="text-sm bg-slate-900 text-slate-100 p-6 rounded-lg overflow-x-auto border border-slate-700/50 shadow-lg">
                <code className="text-purple-400">
{getIframeEmbedCode()}
                </code>
              </pre>
              <Button
                size="sm"
                className="absolute top-3 right-3 bg-slate-800 hover:bg-slate-700 border border-slate-600"
                onClick={handleCopyIframeCode}
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy
              </Button>
            </div>

            {/* Quick Reference - Link to Detailed Instructions */}
            <div className="mt-4 p-3 bg-white rounded-lg border border-purple-200/60">
              <p className="text-sm text-purple-900 font-medium mb-2">Need detailed platform instructions?</p>
              <p className="text-xs text-slate-600">Click the "CMS Platforms" tab above for step-by-step guides for WordPress, Google Sites, Wix, and more.</p>
            </div>
          </div>

          {/* Benefits Comparison */}
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h5 className="font-medium text-blue-900 mb-2">Which option to choose?</h5>
            <div className="grid md:grid-cols-2 gap-4 text-sm text-blue-800">
              <div>
                <strong>JavaScript Widget:</strong> Best for custom websites, single line of code, full customization
              </div>
              <div>
                <strong>iframe Embed:</strong> Perfect for CMS platforms, easy copy-paste, works everywhere without coding
              </div>
            </div>
          </div>
        </div>

        {/* Visual Installation Guide */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="p-6 rounded-lg border border-blue-200/60 bg-blue-50/50">
            <div className="w-12 h-12 rounded-lg bg-blue-100 border border-blue-200/50 flex items-center justify-center mb-4">
              <span className="text-blue-700 font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-slate-900 mb-2">{t('copyTheCode')}</h4>
            <p className="text-sm text-slate-700">
              {t('copyTheCodeDesc')}
            </p>
          </div>

          <div className="p-6 rounded-lg border border-emerald-200/60 bg-emerald-50/50">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 border border-emerald-200/50 flex items-center justify-center mb-4">
              <span className="text-emerald-700 font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-slate-900 mb-2">{t('pasteInWebsite')}</h4>
            <p className="text-sm text-slate-700">
              {t('pasteInWebsiteDesc')}
            </p>
          </div>

          <div className="p-6 rounded-lg border border-purple-200/60 bg-purple-50/50">
            <div className="w-12 h-12 rounded-lg bg-purple-100 border border-purple-200/50 flex items-center justify-center mb-4">
              <span className="text-purple-700 font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-slate-900 mb-2">{t('done')}</h4>
            <p className="text-sm text-slate-700">
              {t('doneDesc')}
            </p>
          </div>
        </div>

        {/* Quick Navigation to Specialized Tabs */}
        <div className="p-4 bg-gradient-to-r from-emerald-50 to-purple-50 rounded-lg border border-blue-200/60">
          <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-blue-600" />
            Need More Help?
          </h4>
          <p className="text-sm text-slate-600 mb-3">
            For detailed platform-specific instructions and advanced integration options, check out the specialized tabs above.
          </p>
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md">📱 Modern Frameworks</span>
            <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded-md">🌐 CMS Platforms</span>
            <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded-md">🔧 Troubleshooting</span>
          </div>
        </div>
      </div>
    </div>
  );
} 